import React, { useEffect, useState, useCallback } from 'react';
import { User } from '../../types/auth';
import { usePredictionMarket, Market } from '../../hooks/usePredictionMarket';
import TxToast from '../common/TxToast';

interface ClaimPageProps {
  user: User | null;
}

interface UserPosition {
  market: Market;
  yesBet: string;
  noBet: string;
  isWinner: boolean;
  hasClaimed: boolean;
}

export default function ClaimPage({ user }: ClaimPageProps) {
  const { markets, loadingMarkets, fetchMarkets, claimWinnings, getUserBet, tx, resetTx } =
    usePredictionMarket();

  const [positions, setPositions] = useState<UserPosition[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [claimHistory, setClaimHistory] = useState<number[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('pm_claim_history') || '[]');
    } catch {
      return [];
    }
  });
  const [activeClaimId, setActiveClaimId] = useState<number | null>(null);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    const eth = (window as any).ethereum;
    if (!eth) return;
    try {
      const accounts: string[] = await eth.request({ method: 'eth_accounts' });
      if (!accounts.length) return;
      const addr = accounts[0];
      setLoadingPositions(true);
      const result: UserPosition[] = [];
      for (const m of markets) {
        const [yes, no] = await Promise.all([
          getUserBet(m.id, addr, true),
          getUserBet(m.id, addr, false),
        ]);
        const yesAmt = parseFloat(yes);
        const noAmt = parseFloat(no);
        if (yesAmt > 0 || noAmt > 0) {
          const isWinner =
            m.resolved &&
            ((m.outcome && yesAmt > 0) || (!m.outcome && noAmt > 0));
          result.push({
            market: m,
            yesBet: yes,
            noBet: no,
            isWinner,
            hasClaimed: claimHistory.includes(m.id),
          });
        }
      }
      setPositions(result);
    } catch (err: any) {
      console.debug('[ClaimPage] loadPositions error:', err?.message);
    } finally {
      setLoadingPositions(false);
    }
  }, [markets, getUserBet, user, claimHistory]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  async function handleClaim(marketId: number) {
    setActiveClaimId(marketId);
    resetTx();
    const success = await claimWinnings(marketId);
    if (success) {
      const updated = [...claimHistory, marketId];
      setClaimHistory(updated);
      localStorage.setItem('pm_claim_history', JSON.stringify(updated));
    }
    setActiveClaimId(null);
    await loadPositions();
  }

  if (!user) {
    return (
      <div className="page-container">
        <div className="access-denied">
          <span className="access-denied-icon">🔑</span>
          <h2>Sign In Required</h2>
          <p>Please sign in to view and claim your winnings.</p>
        </div>
      </div>
    );
  }

  const winningPositions = positions.filter(p => p.isWinner && !p.hasClaimed);
  const otherPositions = positions.filter(p => !p.isWinner || p.hasClaimed);

  function formatDeadline(ts: number) {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleString();
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">🏆 Claim Winnings</h2>
        <p className="page-subtitle">
          Settled markets where you predicted correctly. Claim your ETH payout.
        </p>
      </div>

      <TxToast tx={tx} onDismiss={resetTx} />

      {loadingMarkets || loadingPositions ? (
        <div className="loading-state">Loading your positions…</div>
      ) : positions.length === 0 ? (
        <div className="empty-state">
          You have no positions in any market yet.{' '}
          <span>Head to the Trade tab to get started!</span>
        </div>
      ) : (
        <>
          {/* Claimable winnings */}
          {winningPositions.length > 0 && (
            <section className="claim-section">
              <h3 className="section-title claim-title">
                🎉 Claimable Winnings ({winningPositions.length})
              </h3>
              <div className="claim-list">
                {winningPositions.map(({ market, yesBet, noBet }) => (
                  <div key={market.id} className="claim-card claim-card-winner">
                    <div className="claim-card-header">
                      <span className="market-id">#{market.id}</span>
                      <span className="badge badge-yes">
                        Won: {market.outcome ? 'YES ✓' : 'NO ✗'}
                      </span>
                    </div>
                    <p className="market-question">{market.question}</p>
                    <div className="claim-position-detail">
                      {parseFloat(yesBet) > 0 && (
                        <span className="position-yes">
                          YES bet: {yesBet} ETH
                        </span>
                      )}
                      {parseFloat(noBet) > 0 && (
                        <span className="position-no">
                          NO bet: {noBet} ETH
                        </span>
                      )}
                      <span className="claim-deadline">
                        Resolved · Deadline was {formatDeadline(market.deadline)}
                      </span>
                    </div>
                    <button
                      className="btn-primary btn-full btn-claim"
                      onClick={() => handleClaim(market.id)}
                      disabled={activeClaimId === market.id || tx.status === 'pending'}
                    >
                      {activeClaimId === market.id
                        ? '⏳ Claiming…'
                        : '💸 Claim Winnings'}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Other positions / history */}
          {otherPositions.length > 0 && (
            <section className="claim-section">
              <h3 className="section-title">Position History</h3>
              <div className="claim-list">
                {otherPositions.map(({ market, yesBet, noBet, hasClaimed }) => (
                  <div
                    key={market.id}
                    className={`claim-card${hasClaimed ? ' claim-card-claimed' : ' claim-card-pending'}`}
                  >
                    <div className="claim-card-header">
                      <span className="market-id">#{market.id}</span>
                      {hasClaimed ? (
                        <span className="badge badge-claimed">Claimed ✓</span>
                      ) : market.resolved ? (
                        <span className="badge badge-no">
                          Lost: {market.outcome ? 'NO' : 'YES'} ✗
                        </span>
                      ) : (
                        <span className="badge badge-open">Pending</span>
                      )}
                    </div>
                    <p className="market-question">{market.question}</p>
                    <div className="claim-position-detail">
                      {parseFloat(yesBet) > 0 && (
                        <span className="position-yes">YES: {yesBet} ETH</span>
                      )}
                      {parseFloat(noBet) > 0 && (
                        <span className="position-no">NO: {noBet} ETH</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
